import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { startSurvey, getOptions, selectOption } from "../api/surveyApi";
import "./surveyAccordion.css";

export default function SurveyAccordion() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const parentId = searchParams.get("parentId");
  const stepNo = Number(searchParams.get("stepNo") || 1);

  const [parents, setParents] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [childrenMap, setChildrenMap] = useState({});
  const [selectedChildId, setSelectedChildId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    startSurvey(slug).catch(() => {});
  }, [slug]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    getOptions(slug, parentId)
      .then((res) => {
        if (!mounted) return;
        setParents(res.data.options || []);
        setExpandedId(null);
        setChildrenMap({});
        setSelectedChildId(null);
      })
      .catch((err) => {
        if (err?.response?.status === 401) return navigate("/login");
        alert(err?.response?.data?.message || "Failed to load options");
      })
      .finally(() => mounted && setLoading(false));

    return () => (mounted = false);
  }, [slug, parentId, navigate]);

  const toggleExpand = async (pid) => {
    if (String(expandedId) === String(pid)) {
      setExpandedId(null);
      return;
    }

    setExpandedId(pid);
    setSelectedChildId(null);

    if (childrenMap[pid]) return;

    try {
      setLoadingChildren(true);
      const res = await getOptions(slug, pid);
      setChildrenMap((prev) => ({ ...prev, [pid]: res.data.options || [] }));
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to load sub options");
    } finally {
      setLoadingChildren(false);
    }
  };

  const handleSubmit = async () => {
    const nodeToSubmit = selectedChildId || expandedId;
    if (!nodeToSubmit) return alert("Please select an option");

    try {
      setSubmitting(true);
      const res = await selectOption(slug, Number(nodeToSubmit), stepNo);

      if (res.data.done) {
        navigate(`/survey/${slug}/done`);
        return;
      }

      navigate(
        `/survey/${slug}/accordion?parentId=${nodeToSubmit}&stepNo=${stepNo + 1}`
      );
    } catch (err) {
      alert(err?.response?.data?.message || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    navigate(`/survey/${slug}/done`);
  };

  if (loading) {
    return (
      <div className="sv-accPage">
        <div className="sv-accShell">
          <div className="sv-accHeader sk-card">
            <div className="sk-title" />
            <div className="sk-line" />
          </div>

          <div className="sk-list">
            <div className="sk-item" />
            <div className="sk-item" />
            <div className="sk-item" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sv-accPage">
      <div className="sv-accShell">
        <header className="sv-accHeader">
          <div className="sv-accBadge">Step {stepNo}</div>
          <h2 className="sv-accTitle">Which of the following options interest you?</h2>
          <p className="sv-accSub">
            Select a category, then choose a sub-option (optional).
          </p>
        </header>

        <div className="sv-accList">
          {parents.map((p) => {
            const open = String(expandedId) === String(p.id);
            const children = childrenMap[p.id] || [];
            const hasFetched = !!childrenMap[p.id];

            return (
              <div key={p.id} className={`sv-accCard ${open ? "is-open" : ""}`}>
                <button
                  type="button"
                  className={`sv-parentBtn ${open ? "is-active" : ""}`}
                  onClick={() => toggleExpand(p.id)}
                >
                  <span className="sv-parentText">{p.label}</span>
                  <span className={`sv-chevron ${open ? "is-rot" : ""}`} aria-hidden="true">
                    ▾
                  </span>
                </button>

                {open && (
                  <div className="sv-childrenWrap">
                    {loadingChildren && !hasFetched ? (
                      <div className="sv-miniLoading">Loading options…</div>
                    ) : children.length === 0 ? (
                      <div className="sv-empty">No options found</div>
                    ) : (
                      <div className="sv-childrenGrid">
                        {children.map((c) => {
                          const active = String(selectedChildId) === String(c.id);
                          return (
                            <button
                              key={c.id}
                              type="button"
                              className={`sv-childBtn ${active ? "is-active" : ""}`}
                              onClick={() => setSelectedChildId(c.id)}
                            >
                              <span className={`sv-dot ${active ? "is-on" : ""}`} aria-hidden="true" />
                              {c.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="sv-accFooter">
          <button type="button" className="sv-skipBtn" onClick={handleSkip} disabled={submitting}>
            Skip
          </button>

          <button
            type="button"
            className="sv-submitBtn"
            onClick={handleSubmit}
            disabled={submitting || (!expandedId && !selectedChildId)}
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>

          <div className="sv-footHint">
            {!expandedId && !selectedChildId
              ? "Select a category to continue"
              : selectedChildId
              ? "Sub-option selected ✅"
              : "Category selected ✅"}
          </div>
        </div>
      </div>
    </div>
  );
}